import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import {
  ListingDataSource,
  ListingPageModel
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListingTableComponent } from 'src/app/shared/components/listing-table/listing-table.component';
import { TAB_VALUE } from 'src/app/shared/components/listing-page/listing-page.types';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { OrderDirection } from '../../devices/components/devices.models';
import {
  TWO_WAY_REQUEST_PROCESSING_DEFAULT_SEARCH_TYPE,
  TWO_WAY_REQUEST_PROCESSING_SEARCH_TYPES,
  REQUEST_PROCESSING_SORT_FIELD,
  deviceTypeTableIcon
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import { RequestProcessingService } from './request-processing.service';
import {
  RequestProcessingTable,
  TwoWayRequestItemListResponseDto,
  TwoWayRequestProcessingFieldName
} from './request-processing.types';
import { RequestProcessingListingFiltersSourceService } from './request-processing-listing-filters-source.service';
import { ExportRow } from '@portal/shared/types/export.types';
import { TableData } from '@portal/shared/types/api-response.types';
import { RequestProcessingActionsColumnCancelComponent } from '../components/request-processing-actions-column-cancel/request-processing-actions-column-cancel.component';
import { RequestProcessingSidePanelTitleComponent } from '../components/request-processing-device-side-panel/request-processing-side-panel-title.component';
import { RequestProcessingDeviceSidePanelComponent } from '../components/request-processing-device-side-panel/request-processing-device-side-panel.component';
import { CommandStatusColumnComponent } from '../components/command-status-column.component';
import { RequestProcessingActionsColumnRetryComponent } from '../components/request-processing-actions-column-retry/request-processing-actions-column-retry.component';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { CommDeviceIdColumnComponent } from '../../devices/components/column/comm-device-id-column.component';
import { SerialNumberColumnComponent } from '../../devices/components/column/serial-number-column.component';

@Injectable()
export class RequestProcessingListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly requestProcessingService = inject(RequestProcessingService);
  private readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  private readonly tz = inject(TimezoneService);

  readonly pageId = ListingPageId.TwoWayRequestProcessing;
  readonly pageTitle = TranslateRegistry.get.MAIN_MENU.TITLE.TWO_WAY_REQUEST_PROCESSING;

  readonly searchTypeList = computed(() => TWO_WAY_REQUEST_PROCESSING_SEARCH_TYPES);
  readonly selectedSearchType = computed(() => TWO_WAY_REQUEST_PROCESSING_DEFAULT_SEARCH_TYPE);

  readonly sortField = REQUEST_PROCESSING_SORT_FIELD;
  readonly sorting = computed(() => ({
    columnName: this.sortField.completedDateTime,
    orderDirection: OrderDirection.DESC
  }));

  private readonly hasCancelPermission = toSignal(this.requestProcessingService.cancelPermission$, {
    initialValue: false
  });

  private readonly hasRetryPermission = toSignal(this.requestProcessingService.retryPermission$, {
    initialValue: false
  });

  readonly tableColumns = computed(() => [
    ...this.baseColumns(),
    ...(this.hasCancelPermission() ? this.cancelButtonColumn() : []),
    ...(this.hasRetryPermission() ? this.retryButtonColumn() : [])
  ]);

  readonly baseColumns = signal([
    {
      field: 'commDeviceId',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.COMM_DEVICE_ID,
      width: 200,
      sticky: true,
      sortable: true,
      cmp: CommDeviceIdColumnComponent
    },
    {
      field: 'serialNumber',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.SERIAL_NUMBER,
      width: 200,
      sticky: true,
      sortable: true,
      cmp: SerialNumberColumnComponent
    },
    {
      field: 'deviceType',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.DEVICE_TYPE,
      width: 130,
      sticky: true,
      sortable: true,
      icon: deviceTypeTableIcon
    },
    {
      field: 'command',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.COMMAND,
      width: 220,
      sortable: true
    },
    {
      field: 'commandStatus',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.COMMAND_STATUS,
      width: 180,
      sortable: true,
      cmp: CommandStatusColumnComponent
    },
    {
      field: 'scheduledDateTime',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.SCHEDULED_DATE_TIME,
      width: 220,
      sortable: true
    },
    {
      field: 'startDateTime',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.START_DATE_TIME,
      width: 220,
      sortable: true
    },
    {
      field: 'completedDateTime',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.COMPLETED_DATE_TIME,
      width: 240,
      sortable: true
    },
    {
      field: 'actionName',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.COMMAND_GROUP_NAME,
      width: 200,
      sortable: true
    },
    {
      field: 'imageName',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.IMAGE_NAME,
      width: 200,
      sortable: true
    },
    {
      field: 'user',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.USER,
      width: 180,
      sortable: true
    }
  ]);

  readonly cancelButtonColumn = signal([
    {
      field: 'cancelScheduled',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.CANCEL_SCHEDULED,
      width: 150,
      sortable: false,
      cmp: RequestProcessingActionsColumnCancelComponent
    }
  ]);

  readonly retryButtonColumn = signal([
    {
      field: 'retryScheduled',
      title: TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.VIEW_TABLE.RETRY_FAILED,
      width: 120,
      sortable: false,
      cmp: RequestProcessingActionsColumnRetryComponent
    }
  ]);

  readonly dataSource: ListingDataSource<RequestProcessingTable> = {
    getAll: tableState => {
      return this.requestProcessingService.getTableData({
        ...tableState,
        filters: [
          ...(tableState.filters ?? []).map(f => ({
            ...f,
            filteredValues: [
              TwoWayRequestProcessingFieldName.EndDateTimeUtc,
              TwoWayRequestProcessingFieldName.ScheduledDateTimeUtc,
              TwoWayRequestProcessingFieldName.StartDateTimeUtc
            ].includes(f.filterType)
              ? this.tz.getShiftedDatepickerRangeStringified({
                  start: new Date(f.filteredValues[0]),
                  end: new Date(f.filteredValues[1])
                })
              : f.filteredValues
          }))
        ]
      });
    },
    total: 0
  };

  readonly exportConfig = {
    fileName: 'Export-2-Way_RequestProcessing',
    mapData: (data: TableData<TwoWayRequestItemListResponseDto>): ExportRow[] => {
      return data.data.map(request => ({
        deviceId: { title: 'Device ID', value: request.deviceId },
        serialNumber: { title: 'Serial Number', value: request.serialNumber },
        commDeviceId: { title: 'Comm Device ID', value: request.commDeviceId },
        deviceType: { title: 'Device Type', value: request.deviceType },
        command: { title: 'Command', value: request.command },
        commandStatus: { title: 'Command Status', value: request.commandStatus },
        commandGroupName: { title: 'Command Group Name', value: request.actionName },
        createdOnDateTime: { title: 'Created On', value: request.createdOnDateTime },
        scheduledDateTime: { title: 'Scheduled', value: request.scheduledDateTime },
        startDateTime: { title: 'Started', value: request.startDateTime },
        completedDateTime: { title: 'Completed', value: request.completedDateTime },
        imageName: { title: 'Image Name', value: request.imageName },
        user: { title: 'User', value: request.user }
      }));
    }
  };

  readonly tableCmp = ListingTableComponent;
  readonly defaultTab = TAB_VALUE.Table;
  readonly selectionFieldName = 'deviceUID';
  readonly sidePanelBodyCmp = RequestProcessingDeviceSidePanelComponent;
  readonly sidePanelTitleCmp = RequestProcessingSidePanelTitleComponent;
  readonly exportButtonVisible = true;

  readonly filterSource = inject(RequestProcessingListingFiltersSourceService);

  constructor() {
    super();
    this.listingPageModelRegistry.add(this.pageId, this);
  }
}
